import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ApiError } from '@/shared/api/client'
import { useJoinLeague } from '@/features/league/hooks/useUserLeagues'
import styles from './HomeEmptyState.module.css'
import { LogoMark } from '@/shared/components/layout/Logo'

function formatCode(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7)
  if (clean.length <= 3) return clean
  return `${clean.slice(0, 3)}-${clean.slice(3)}`
}

const CODE_REGEX = /^[A-Z0-9]{3}-[A-Z0-9]{4}$/

function getInputError(code: string): string {
  if (code.length === 0) return ''
  if (!CODE_REGEX.test(code)) return 'Código incompleto. O formato é XXX-XXXX.'
  return ''
}

function getDaysUntilTournament(): number {
  const tournamentStart = new Date('2026-06-11T00:00:00Z')
  const diff = tournamentStart.getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

export function HomeEmptyState() {
  const navigate = useNavigate()

  const [isJoinExpanded, setIsJoinExpanded] = useState(false)
  const [code, setCode]                     = useState('')
  const [teamName, setTeamName]             = useState('')
  const [codeError, setCodeError]           = useState('')
  const [codeTouched, setCodeTouched]       = useState(false)

  const joinLeagueMutation = useJoinLeague()
  const isCodeValid = CODE_REGEX.test(code)
  const daysUntilTournament = getDaysUntilTournament()

  const serverError: string = (() => {
    const err = joinLeagueMutation.error
    if (!err) return ''
    if (err instanceof ApiError) {
      switch (err.errorCode) {
        case 'invalid_invite_code':   return 'Código inválido. Verifique e tente novamente.'
        case 'invite_code_not_found': return 'Liga não encontrada. Verifique o código.'
        case 'already_member':        return 'Você já participa desta liga.'
        case 'league_full':           return 'Esta liga atingiu o limite de participantes.'
        case 'league_limit_reached':  return 'Você atingiu o limite de ligas do seu plano.'
        case 'team_name_taken':       return 'Esse nome de time já está em uso nesta liga.'
        default:                      return ''
      }
    }
    return ''
  })()

  const displayedError = codeError || serverError

  function handleCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const formatted = formatCode(e.target.value)
    setCode(formatted)
    if (codeError) setCodeError('')
  }

  function handleCodeBlur() {
    setCodeTouched(true)
    setCodeError(getInputError(code))
  }

  function handleJoinSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isCodeValid) {
      setCodeTouched(true)
      setCodeError(getInputError(code))
      return
    }
    setCodeError('')
    joinLeagueMutation.mutate({ code, teamName: teamName.trim() || undefined })
  }

  return (
    <div className={styles.emptyPage}>
      <div className={styles.emptyInner}>

        <LogoMark size={56} />

        <p className={styles.emptyEyebrow}>SEM LIGAS AINDA</p>

        <h1 className={styles.emptyHeadline}>
          MONTE SEU PRIMEIRO<br />
          TIME EM <span className={styles.acc}>90 SEGUNDOS</span>
        </h1>

        <p className={styles.emptySub}>
          Crie sua liga privada, convide amigos e gerencie seu elenco para a Copa. É tudo em tempo real.
        </p>

        <div className={styles.emptyActions}>
          <button
            type="button"
            className={styles.btnHeroPrimary}
            onClick={() => void navigate({ to: '/league/new' })}
          >
            CRIAR LIGA →
          </button>
          <button
            type="button"
            className={styles.btnHeroSecondary}
            onClick={() => setIsJoinExpanded((v) => !v)}
            aria-expanded={isJoinExpanded}
            aria-controls="join-form"
          >
            ENTRAR EM LIGA
          </button>
        </div>

        {/* Join form — revealed when secondary button is clicked */}
        <form
          id="join-form"
          className={styles.joinForm}
          data-expanded={isJoinExpanded ? true : undefined}
          onSubmit={handleJoinSubmit}
          noValidate
        >
          <div className={styles.joinFormInner}>
            <input
              className={styles.codeInput}
              data-error={codeTouched && !!displayedError ? true : undefined}
              data-valid={isCodeValid ? true : undefined}
              type="text"
              value={code}
              onChange={handleCodeChange}
              onBlur={handleCodeBlur}
              placeholder="Ex: OSI-4729"
              maxLength={8}
              autoComplete="off"
              spellCheck={false}
              aria-label="Código de convite da liga"
              aria-describedby={displayedError ? 'join-code-error' : undefined}
              aria-invalid={(codeTouched && !!codeError) || !!serverError}
            />
            <input
              className={styles.codeInput}
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Nome do seu time (opcional)"
              maxLength={30}
              autoComplete="off"
              spellCheck={false}
              aria-label="Nome do seu time nesta liga (opcional)"
            />
            {displayedError && (
              <span className={styles.inputError} id="join-code-error" role="alert">
                {displayedError}
              </span>
            )}
            <button
              type="submit"
              className={styles.btnJoin}
              disabled={!isCodeValid || joinLeagueMutation.isPending}
            >
              {joinLeagueMutation.isPending ? 'ENTRANDO...' : 'CONFIRMAR ENTRADA'}
            </button>
          </div>
        </form>

        {/* Countdown stat */}
        <div className={styles.emptyStat}>
          <div className={styles.emptyStatVal}>{daysUntilTournament}</div>
          <div className={styles.emptyStatKey}>Dias até a Copa</div>
        </div>

      </div>

      {/* Bottom tips */}
      <div className={styles.emptyTips}>
        <div className={styles.emptyTip}>
          <div className={styles.emptyTipIcon}>⚽</div>
          <span className={styles.emptyTipText}>~830 jogadores da Copa disponíveis</span>
        </div>
        <div className={styles.emptyTip}>
          <div className={styles.emptyTipIcon}>⚡</div>
          <span className={styles.emptyTipText}>Placar atualiza em tempo real</span>
        </div>
        <div className={styles.emptyTip}>
          <div className={styles.emptyTipIcon}>🔒</div>
          <span className={styles.emptyTipText}>Liga privada com convite</span>
        </div>
      </div>

    </div>
  )
}
